import { useEffect, useState } from "react";
import { apiConstants, post, postGeneric } from "../../common/constants";
import {
    Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, IconButton, CircularProgress, Stack, Collapse,
} from "@mui/material";
import {
    AddPhotoAlternateOutlined, DeleteOutline,
    CloudUploadOutlined, CloseOutlined, ZoomOutMapOutlined,
    Upload as UploadIcon,
} from "@mui/icons-material";
import { ImageLightbox, ProductImageThumbs, Thumb, imagesOf } from "../../common/ImageLightbox";
import { useGetAllMerchantsIdAndNameQuery } from "../../redux/commonApiCalls";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { formatCurrency } from "../../common/util";
import PageTitle from "../../components/PageTitle";
import { useForm } from "react-hook-form";
import { FormProvider, RHFFileField, RHFTextField } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { useSnackbar } from "notistack";
import { LoadingButton } from "@mui/lab";
import {
    FilterCard, FilterCardContent, FilterRow, FilterField, FieldLabel,
    FilterActions, ApplyButton, ResetButton,
} from "../../common/FilterBar";

// ── Local design tokens — align with the panel's current look (KCI / Order Details:
//    navy/muted type ramp, soft-shadow cards, tinted pills). ─────────────────────
const T = { NAVY: '#212B36', MUTED: '#637381', BORDER: '#E7EAF0', FONT: 'Poppins, sans-serif' };
const cardShell = {
    bgcolor: '#fff', border: `1px solid ${T.BORDER}`, borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden',
};

const statusTone = (s) => {
    const v = String(s || '').toUpperCase();
    if (['ACTIVE', 'ENABLED', 'LIVE'].includes(v)) return { bg: '#E9F7EF', fg: '#0F7B4F' };
    if (['INACTIVE', 'DISABLED', 'BLOCKED', 'ARCHIVED'].includes(v)) return { bg: '#FDECEA', fg: '#C62828' };
    return { bg: '#EEF1F5', fg: '#637381' };
};

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.75 }}>{children}</Box>
);
const CellLine = ({ children, muted, bold, mono, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4, color: muted ? T.MUTED : T.NAVY, fontWeight: bold ? 600 : 400,
        fontFamily: mono ? 'monospace' : T.FONT,
        ...(ellipsis
            ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
            : { whiteSpace: 'normal', wordBreak: 'break-word' }),
    }}>{children}</Box>
);
const Pill = ({ tone, children }) => (
    <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', px: 0.9, py: 0.25, borderRadius: '6px',
        fontSize: 11, fontWeight: 700, lineHeight: 1.6, bgcolor: tone.bg, color: tone.fg, fontFamily: T.FONT,
    }}>{children}</Box>
);

function ProductMaster() {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [reload, setReload] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [filters, setFilters] = useState([]);
    const [showUpload, setShowUpload] = useState(false);
    const [manageProduct, setManageProduct] = useState(null);
    const [lightbox, setLightbox] = useState(null); // { images, index }
    const { data: merchant } = useGetAllMerchantsIdAndNameQuery();

    useEffect(() => {
        const request = {
            collection: "product_master",
            offset: filters.length > 0 ? 0 : page * sizePerPage,
            limit: sizePerPage,
            skipCount: true,
            countOnly: false,
            sort: { field: "createdDate", direction: "DESC" },
            andfilter: filters,
        };
        post(apiConstants.REPORT_GENERIC_SEARCH, request).then((res) => {
            setData(res?.data?.data);
        });
    }, [sizePerPage, page, filters, reload]);

    const merchantName = (id) => merchant?.find((m) => m.id === id)?.name || '—';

    const columns = [
        {
            field: "name", headerName: "Product", flex: 1.8, minWidth: 250, sortable: false,
            renderCell: ({ row }) => {
                const imgs = imagesOf(row);
                return (
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 0.75, width: '100%' }}>
                        <Thumb src={imgs[0]} size={46} onOpen={() => setLightbox({ images: imgs, index: 0 })} />
                        <Box sx={{ minWidth: 0 }}>
                            <CellLine bold>{row.name || '—'}</CellLine>
                            <CellLine muted mono>{row.skuCode || '—'}</CellLine>
                        </Box>
                    </Stack>
                );
            },
        },
        {
            field: "merchantId", headerName: "Merchant", flex: 1, minWidth: 150, sortable: false,
            renderCell: ({ row }) => <CellLine>{merchantName(row.merchantId)}</CellLine>,
        },
        {
            field: "brand", headerName: "Brand / Category", flex: 1.2, minWidth: 160, sortable: false,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.brand || '—'}</CellLine>
                    <CellLine muted>{[row.category, row.subCategory].filter(Boolean).join(' · ') || 'Uncategorised'}</CellLine>
                </CellStack>
            ),
        },
        {
            field: "productStatus", headerName: "Status", minWidth: 110, sortable: false,
            renderCell: ({ row }) => (row.productStatus
                ? <Pill tone={statusTone(row.productStatus)}>{row.productStatus}</Pill>
                : <CellLine muted>—</CellLine>),
        },
        {
            field: "weight", headerName: "Weight / Dimensions", flex: 1.1, minWidth: 160, sortable: false,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.weight ? `${row.weight} g` : '—'}</CellLine>
                    <CellLine muted>{(row.length && row.breath && row.height) ? `${row.length} × ${row.breath} × ${row.height} cm` : 'No dimensions'}</CellLine>
                </CellStack>
            ),
        },
        {
            field: "mrp", headerName: "MRP", minWidth: 100, sortable: false,
            renderCell: ({ row }) => <CellLine bold>{row.mrp ? formatCurrency(row.mrp) : '—'}</CellLine>,
        },
        {
            field: "images", headerName: "Images", minWidth: 180, sortable: false,
            renderCell: ({ row }) => {
                const imgs = imagesOf(row);
                return (
                    <Stack spacing={0.75} alignItems="flex-start" sx={{ py: 0.75 }}>
                        <ProductImageThumbs images={imgs} size={32} empty="No images yet" />
                        <Button size="small" variant="outlined"
                            startIcon={<AddPhotoAlternateOutlined sx={{ fontSize: 15 }} />}
                            onClick={() => setManageProduct(row)}
                            sx={{
                                textTransform: 'none', fontWeight: 600, fontSize: 11.5, py: 0.2, px: 1,
                                borderColor: T.BORDER, color: '#1565c0',
                                '&:hover': { borderColor: '#1565c0', bgcolor: '#e8f1fc' },
                            }}>
                            {imgs.length ? `Manage (${imgs.length})` : 'Add image'}
                        </Button>
                    </Stack>
                );
            },
        },
    ];

    const methods = useForm({ defaultValues: { merchant: '', skuCode: '', name: '' } });
    const { handleSubmit, reset } = methods;

    const onSubmit = (values) => {
        const andfilter = [];
        if (values.merchant) andfilter.push({ field: "merchantId", operator: "eq", value: values.merchant });
        if (values.skuCode) andfilter.push({ field: "skuCode", operator: "eq", value: values.skuCode });
        if (values.name) andfilter.push({ field: "name", operator: "eq", value: values.name });
        setFilters(andfilter);
        setPage(0);
    };

    const handleReset = () => {
        reset({ merchant: '', skuCode: '', name: '' });
        setFilters([]);
        setPage(0);
    };

    const rows = (data || []).map((r, i) => ({ ...r, _rowId: i }));

    return (
        <Box>
            <PageTitle>SKU Master</PageTitle>

            {/* Heading */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.NAVY, fontFamily: T.FONT, lineHeight: 1.2 }}>Product Master</Typography>
                    <Typography sx={{ fontSize: 13, color: T.MUTED, mt: 0.25 }}>
                        Every merchant's catalog SKUs — filter, review specs &amp; pricing, and manage product images.
                    </Typography>
                </Box>
                <Button variant="contained" disableElevation
                    startIcon={showUpload ? <CloseOutlined /> : <CloudUploadOutlined />}
                    onClick={() => setShowUpload((s) => !s)}
                    sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: 13.5, px: 2.5, py: 1, borderRadius: '10px',
                        bgcolor: showUpload ? '#0d47a1' : '#1565c0', color: '#fff',
                        boxShadow: '0 2px 6px rgba(21,101,192,0.30)',
                        '&:hover': { bgcolor: '#0d47a1', boxShadow: '0 3px 10px rgba(21,101,192,0.38)' },
                    }}>
                    {showUpload ? 'Close bulk upload' : 'Bulk upload products'}
                </Button>
            </Box>

            {/* Bulk upload (collapsible) */}
            <Collapse in={showUpload} unmountOnExit>
                <Box sx={{ ...cardShell, borderColor: '#BBD3F0', bgcolor: '#F5F9FF', p: 2.5, mb: 2, borderLeft: '4px solid #1565c0' }}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
                        <CloudUploadOutlined sx={{ fontSize: 20, color: '#1565c0' }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.NAVY }}>Bulk upload product master</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: T.MUTED, mb: 2 }}>
                        Upload the product-master sheet for a merchant. Download the sample to see the expected columns.
                    </Typography>
                    <UploadProduct setReload={setReload} />
                </Box>
            </Collapse>

            {/* Filters */}
            <FilterCard>
                <FilterCardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <FilterRow>
                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth placeholder="All merchants" />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>Product Name</FieldLabel>
                                <RHFTextField name="name" label="" placeholder="Product name" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                            <FilterField>
                                <FieldLabel>SKU Code</FieldLabel>
                                <RHFTextField name="skuCode" label="" placeholder="SKU code" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>
                            <FilterActions>
                                <ApplyButton type="submit" variant="contained" size="small">Filter</ApplyButton>
                                <ResetButton type="button" variant="contained" size="small" onClick={handleReset}>Reset</ResetButton>
                            </FilterActions>
                        </FilterRow>
                    </FormProvider>
                </FilterCardContent>
            </FilterCard>

            {/* Catalog grid */}
            <Box sx={cardShell}>
                <ProzoDataGrid
                    columns={columns}
                    rows={rows}
                    getRowId={(r) => r._rowId}
                    getRowHeight={() => 'auto'}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                />
            </Box>

            {manageProduct && (
                <ManageImagesDialog
                    product={manageProduct}
                    onClose={() => setManageProduct(null)}
                    onSaved={() => setReload((r) => r + 1)}
                />
            )}

            {lightbox && (
                <ImageLightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} />
            )}
        </Box>
    );
}

export default ProductMaster;


const UploadProduct = ({ setReload }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false)


    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                file: Yup.mixed().required("Please select file to upload"),
                merchant: Yup.string().required("Please select merchant")
            })
        ),
        defaultValues: {
            file: null,
            merchant: '',
        }
    });
    const {
        handleSubmit,
        reset,
    } = methods;

    const onSubmit = (data) => {
        setIsLoading(true)
        const formData = new FormData();
        formData.append("skuMaster", data.file[0]);
        postGeneric(`${apiConstants.UPLOAD_SKU_DETAILS}?merchantId=${data.merchant}`, formData).then((res) => {
            enqueueSnackbar("Successfully Uploaded", { variant: 'success' })
            setReload(reload => reload + 1)
            reset()
            setIsLoading(false)
        }).catch((err) => {
            console.log(err);
            enqueueSnackbar("Some error occurred", { variant: 'error' })
            setIsLoading(false)
            reset()
        })
    }

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                    <MerchantFilterCached fullWidth placeholder="Select merchant" />
                </Grid>
                <Grid item xs={12} md={3} overflow={'hidden'}>
                    <RHFFileField name="file" label="Upload SKU Details" color="secondary" variant="filled" />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <LoadingButton loading={isLoading} type="submit" variant="contained" size="medium" disableElevation
                            startIcon={<UploadIcon />}
                            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                        >Upload</LoadingButton>
                        <Button variant="outlined" size="medium"
                            onClick={() => {
                                reset({
                                    merchant: '',
                                    file: null,
                                })
                            }}
                            sx={{ textTransform: 'none', fontWeight: 600, color: T.MUTED, borderColor: T.BORDER, '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }}
                        >Reset</Button>
                        <DownloadSampleFile fileName={"PRODUCT_MASTER_SAMPLE"} label='Download sample' size="medium" />
                    </Stack>
                </Grid>
            </Grid>
        </FormProvider>
    )
}

// Exact header row of the product-master upload sheet (GET order/Download_samples
// ?fileName=PRODUCT_MASTER_SAMPLE). image1..image10 hold the catalog image URLs.
const PM_SHEET_HEADERS = [
    'skuCode', 'productId', 'name', 'brand', 'category', 'subCategory', 'mrp', 'eanCode', 'upcCode',
    'hsnCode', 'uom', 'weight', 'length', 'breath', 'height', 'productStatus', 'imageUploaded',
    'image1', 'image2', 'image3', 'image4', 'image5', 'image6', 'image7', 'image8', 'image9', 'image10',
];
const csvCell = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
// One-row product-master CSV: the product's existing fields (so nothing is wiped
// by the upsert) + the images spread across image1..image10.
const buildProductMasterCsv = (product, images) => {
    const imgs = (images || []).filter(Boolean).slice(0, 10);
    const imageCols = Array.from({ length: 10 }, (_, i) => imgs[i] || '');
    const row = [
        product.skuCode, product.productId, product.name, product.brand, product.category, product.subCategory,
        product.mrp, product.eanCode, product.upcCode, product.hsnCode, product.uom,
        product.weight, product.length, product.breath, product.height, product.productStatus,
        imgs.length ? 'Yes' : 'No', ...imageCols,
    ];
    return `${PM_SHEET_HEADERS.join(',')}\n${row.map(csvCell).join(',')}\n`;
};

/**
 * Add / manage the catalog images for a single existing product.
 * Flow: pick a file -> POST /tools/uploadImagesNonExpire (attachments[] + folder) -> get the S3 URL,
 *       merged into a local `images` array; on Save we persist via the product-master bulk-upload
 *       endpoint (product/uploadSkuDetails?merchantId=<OID>) with a one-row sheet.
 *
 * Why the bulk-upload path (not generic/update): product_master's generic/search returns `_id` as
 * { timestamp, date } with `id: null` — no hex ObjectId — and generic/update requires a hex `id`
 * (it rejects an andfilter with "Nothing to update - Id is missing"). There is no product/update
 * endpoint either. The ONLY write path is the sheet upsert (keyed by merchantId + skuCode), which
 * supports images via image1..image10 — so we generate that sheet in-browser and upload it. We
 * resend the product's existing columns so the upsert doesn't blank them. [[product-master-image-write-gap]]
 */
function ManageImagesDialog({ product, onClose, onSaved }) {
    const { enqueueSnackbar } = useSnackbar();
    const initial = imagesOf(product);

    const [images, setImages] = useState(initial);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [viewIdx, setViewIdx] = useState(null); // lightbox index

    const dirty = JSON.stringify(images) !== JSON.stringify(initial);
    const full = images.length >= 5;

    const handlePick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        if (!file) return;
        if (full) {
            enqueueSnackbar('A product can have at most 5 images', { variant: 'warning' });
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('attachments[]', file);
            formData.append('folder', 'product_master');
            const res = await post(apiConstants.UPLOAD_LOGO_IMAGE, formData);
            const url = res?.data?.[0];
            if (res.status === 200 && url) {
                setImages((prev) => [...prev, url].slice(0, 5));
            } else {
                enqueueSnackbar('Upload did not return an image URL', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Image upload failed', { variant: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const removeAt = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

    const handleSave = async () => {
        if (!product.merchantId || !product.skuCode) {
            enqueueSnackbar('Missing merchant or SKU on this product — cannot save', { variant: 'error' });
            return;
        }
        setSaving(true);
        try {
            // Persist via the product-master bulk-upload upsert (the only write path).
            // Build a one-row sheet (existing fields + images) and POST as `skuMaster`.
            const csv = buildProductMasterCsv(product, images);
            const file = new File([csv], `${product.skuCode}_images.csv`, { type: 'text/csv' });
            const formData = new FormData();
            formData.append('skuMaster', file);
            const res = await postGeneric(
                `${apiConstants.UPLOAD_SKU_DETAILS}?merchantId=${encodeURIComponent(product.merchantId)}`,
                formData
            );
            if (res.status === 200) {
                enqueueSnackbar('Images saved', { variant: 'success' });
                onSaved?.();
                onClose();
            } else {
                enqueueSnackbar(typeof res.data === 'string' ? res.data : 'Could not save images', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || err.response?.data || 'Could not save images', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ fontWeight: 700, fontFamily: T.FONT, color: T.NAVY }}>
                Product images
                <Typography variant="body2" sx={{ color: T.MUTED, fontFamily: T.FONT }}>
                    {product.name || product.skuCode} &middot; SKU {product.skuCode}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {images.length === 0 && (
                    <Typography variant="body2" sx={{ color: T.MUTED, mb: 2 }}>
                        No images yet. Add up to 5.
                    </Typography>
                )}
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {images.map((url, i) => (
                        <Box key={i} sx={{ position: 'relative', width: 96, height: 96 }}>
                            <Box role="button" title="View image" onClick={() => setViewIdx(i)}
                                sx={{
                                    cursor: 'pointer', lineHeight: 0, borderRadius: '8px',
                                    '&:hover .thumb-zoom': { opacity: 1 }, '&:hover img': { filter: 'brightness(0.92)' },
                                }}>
                                <img
                                    src={url}
                                    alt={`product-${i}`}
                                    style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: `1px solid ${T.BORDER}`, display: 'block' }}
                                />
                                <ZoomOutMapOutlined className="thumb-zoom" sx={{ position: 'absolute', bottom: 4, right: 4, fontSize: 16, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '4px', p: '1px', opacity: 0, transition: 'opacity .15s ease' }} />
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => removeAt(i)}
                                sx={{ position: 'absolute', top: -10, right: -10, bgcolor: '#fff', border: `1px solid ${T.BORDER}`, '&:hover': { bgcolor: '#FEE2E2' } }}
                            >
                                <DeleteOutline sx={{ fontSize: 16, color: '#DC2626' }} />
                            </IconButton>
                        </Box>
                    ))}
                </Stack>

                <Button
                    component="label"
                    variant="outlined"
                    disabled={uploading || full}
                    startIcon={uploading ? <CircularProgress size={16} /> : <AddPhotoAlternateOutlined />}
                    sx={{ textTransform: 'none' }}
                >
                    {uploading ? 'Uploading…' : full ? 'Maximum 5 images' : 'Add image'}
                    <input type="file" accept="image/*" hidden onChange={handlePick} />
                </Button>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
                <LoadingButton
                    loading={saving}
                    disabled={!dirty || uploading}
                    variant="contained"
                    onClick={handleSave}
                    sx={{ textTransform: 'none' }}
                >
                    Save changes
                </LoadingButton>
            </DialogActions>
        </Dialog>
        {viewIdx !== null && (
            <ImageLightbox images={images} index={viewIdx} onClose={() => setViewIdx(null)} />
        )}
        </>
    );
}
